import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { CancellationFeedback } from '@features/subscription/api';

const REASONS = [
  ['too_expensive', '💸', 'Je to pro mě příliš drahé'],
  ['low_usage', '💤', 'Aplikaci používám málo'],
  ['missing_feature', '🧩', 'Chybí mi nějaká funkce'],
  ['not_a_fit', '😕', 'Aplikace mi nevyhovuje'],
  ['bug', '🐛', 'Něco nefungovalo'],
  ['switching', '🔄', 'Přecházím jinam'],
  ['other', '💬', 'Něco jiného'],
] as const;

type Props = {
  open: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (feedback: CancellationFeedback) => void;
};

export default function CancellationSurveyDialog({ open, isSubmitting, onClose, onConfirm }: Props) {
  const [reason, setReason] = useState<string | null>(null);
  const [detail, setDetail] = useState('');
  const [desiredPrice, setDesiredPrice] = useState('');
  const [callRequested, setCallRequested] = useState(false);
  const [phone, setPhone] = useState('');
  const [done, setDone] = useState(false);

  const reset = () => {
    setReason(null); setDetail(''); setDesiredPrice(''); setCallRequested(false); setPhone(''); setDone(false);
  };
  const close = () => { reset(); onClose(); };
  const submit = (selectedReason = reason ?? 'skipped') => {
    onConfirm({
      reason: selectedReason,
      detail: detail || undefined,
      desiredPrice: desiredPrice ? Number(desiredPrice) : undefined,
      callRequested,
      phone: phone || undefined,
    });
    setDone(true);
  };

  const helper = reason === 'missing_feature' ? 'Co Vám chybělo? Klidně jen dvě slova.'
    : reason === 'bug' ? 'Co nefungovalo? I krátký popis mi pomůže.'
    : reason === 'too_expensive' ? 'Jaká cena za měsíc by Vám vzhledem k používání dávala smysl?'
    : 'Chcete k tomu něco doplnit? (nepovinné)';

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : close} maxWidth="sm" fullWidth>
      {done ? (
        <>
          <DialogTitle>Hotovo. Předplatné je zrušené. ❤️</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography>Děkuji, že jste Kadeřník Plus vyzkoušela. Jsem Monika a Vaše odpověď se dostane přímo ke mně.</Typography>
              <Typography color="text.secondary">Vaše data zůstanou zachována podle podmínek služby. Kdyby Vás napadlo, co můžeme udělat lépe, můžete mi kdykoli napsat.</Typography>
              <Alert severity="info">Pokud chcete probrat pozastavení nebo návrat k aplikaci, zaškrtněte prosbu o telefonát při rušení — ozvu se osobně.</Alert>
            </Stack>
          </DialogContent>
          <DialogActions><Button onClick={close}>Zavřít</Button></DialogActions>
        </>
      ) : !reason ? (
        <>
          <DialogTitle>Než odejdete — mohu se zeptat proč? 👋</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }} color="text.secondary">Jsem Monika a Kadeřník Plus vyvíjím osobně. Každou odpověď si opravdu přečtu. Stačí jedno kliknutí.</Typography>
            <Stack spacing={1}>{REASONS.map(([value, icon, label]) => <Button key={value} variant="outlined" onClick={() => setReason(value)} sx={{ justifyContent: 'flex-start', py: 1.25, textTransform: 'none' }}>{icon}&nbsp;&nbsp;{label}</Button>)}</Stack>
          </DialogContent>
          <DialogActions><Button color="inherit" disabled={isSubmitting} onClick={() => submit('skipped')}>Přeskočit a zrušit</Button><Button onClick={close}>Zpět</Button></DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>{reason === 'missing_feature' ? 'Tak to mě zajímá. 👀' : reason === 'too_expensive' ? 'Rozumím.' : 'Děkuji za upřímnost.'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              {reason === 'low_usage' && <Alert severity="info">Možná nemusíte odcházet úplně. Pokud chcete probrat pozastavení na 3 měsíce bez placení a se zachováním dat, požádejte níže o telefonát.</Alert>}
              {reason === 'too_expensive' ? <TextField label="Kč / měsíc (nepovinné)" type="number" value={desiredPrice} onChange={e => setDesiredPrice(e.target.value)} /> : <TextField label={helper} multiline minRows={3} value={detail} onChange={e => setDetail(e.target.value)} />}
              <Box><FormControlLabel control={<Checkbox checked={callRequested} onChange={e => setCallRequested(e.target.checked)} />} label="Může mi Monika krátce zavolat?" />
                {callRequested && <TextField fullWidth label="Telefon (nepovinné, pokud ho nemáme v profilu)" value={phone} onChange={e => setPhone(e.target.value)} />}</Box>
            </Stack>
          </DialogContent>
          <DialogActions><Button disabled={isSubmitting} onClick={() => setReason(null)}>Zpět</Button><Button variant="contained" disabled={isSubmitting} onClick={() => submit()}>{isSubmitting ? 'Ukončuji…' : 'Odeslat a zrušit předplatné'}</Button></DialogActions>
        </>
      )}
    </Dialog>
  );
}
